// @ts-nocheck
import React, { forwardRef, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import {
  defaultsDeep,
  extend,
  clone,
  uniqueId,
  values as _values,
  compact,
  find,
  isObject,
  delay,
  get,
  isEqual,
  toString,
} from 'lodash'
import * as PropTypes from 'prop-types'
// Data
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Input,
  Select as MuiSelect,
  FilledInput,
  OutlinedInput
} from '@mui/material'
import { KeyboardArrowDown } from '@mui/icons-material'
import { Forms } from '../schema'
import { events } from '@ggs/utils'
// Components
import Checkbox from './Checkbox'
import TextField from './TextField'
// Assets
import './_Select.scss'


const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    elevation: 0,
    style: {
      borderRadius: 0,
      border: '1px solid #ccc',
      marginTop: '-1px',
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
}

const propTypes = {
  style: PropTypes.oneOf(_values(Forms.SelectStyles).concat([undefined, ''])),
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      disabled: PropTypes.bool,
      name: PropTypes.string,
      checked: PropTypes.bool,
    })
  ),
}

const defaultProps = {
  style: '',
  label: '',
  options: [],
  placeholder: 'Select',
  variant: 'outlined',
  showLabelOnTop: false
}

// eslint-disable-next-line react/display-name
const Select = forwardRef(
  ({ className, name, onChange, freeSolo = false, value: formikValue, ...props }, ref) => {
    const {
      style,
      label,
      valueKey = 'value',
      labelKey = 'label',
      variant,
      placeholder,
      showLabelOnTop,
    } = defaultsDeep(props, defaultProps)
    const selectId = uniqueId(`mui-select-${style}-`)
    const commonProps = {
      ref,
      name,
      id: selectId,
      placeholder,
    }
    const [defaultValue, setDefaultValue] = useState(null)
    const [options, setOptions] = useState(clone(props.options))
    const disabled = props?.disabled || false

    const [selectedValue, setSelectedValue] = useState(() => {
      let initialValue = formikValue || props.defaultValue?.value || props.defaultValue || ''
      if (style && style !== Forms.SelectStyles.autocomplete) {
        initialValue = Array.isArray(formikValue) ? formikValue : []
      }
      // console.log(`Select::fieldValue ${name}`, style, initialValue)
      return initialValue
    })

    const handleChange = (e, option) => {
      const input = ref?.current?.querySelector('input')

      switch (style) {
        // For these style, synthetically add the custom selection option for formik.
        case Forms.SelectStyles.autocomplete:
          // eslint-disable-next-line no-param-reassign
          e = events.syntheticInputEvent(name, { value: get(option, 'value', input?.value || '') })
          break
      }
      // setSelectedValue((e.target.value))
      onChange(e)
      // console.log('Select::handleChange', e.target.value, option)
    }

    useEffect(() => {
      // console.log("FormikValue:", formikValue)
      // We should only need to update from fomrik if the value was changed
      formikValue && setSelectedValue(formikValue)
    }, [formikValue, defaultValue])

    const autocompleteDefaultProps = {
      options,
      freeSolo,
      getOptionLabel: (option) => option.label ?? '',
    }

    const renderAttributes = useMemo(() => {
      const attributes = {}
      const safeSelectedValue = toString(selectedValue)
      const optionExists = find(options, { value: safeSelectedValue })
      attributes.onChange = handleChange

      switch (style) {
        case Forms.SelectStyles.multiple:
        case Forms.SelectStyles.multipleCheckboxes:
          attributes.multiple = true
          attributes.value = !Array.isArray(selectedValue) ? [] : selectedValue
          attributes.renderValue = (selected) =>
            compact(
              options.map((o) => (selected.indexOf(o.value || o.label) !== -1 ? o.label : null))
            ).join(', ')
          break
        case Forms.SelectStyles.checkboxes:
        default:
          attributes.multiple = false
          attributes.renderValue = (selected) => (find(options, ['value', selected]) || {}).label
          if (safeSelectedValue && optionExists) {
            attributes.value = safeSelectedValue
          }
      }
      return attributes
    }, [style, selectedValue, options])

    // console.log(`Select::render ${name}`, { style, formikValue })

    useLayoutEffect(() => {
      switch (style) {
        case Forms.SelectStyles.autocomplete:
          if (isObject(defaultValue)) {
            delay(() => {
              handleChange(null, defaultValue)
              const input = ref?.current?.querySelector('input')
              if (input) {
                input.value = defaultValue
              }
            }, 1000)
          }
          break
      }
    }, [])

    useEffect(() => {
      const normDefaultValue = props.defaultValue?.toUpperCase() || ''
      const defaultOptionMatch = find(props.options, [valueKey, normDefaultValue])
      const currentOption = defaultOptionMatch || {
        [valueKey]: props.defaultValue,
        [labelKey]: props.defaultValue,
      }

      // No force load, assume no default as there won't be any options to work against.
      if (currentOption) {
        setOptions([currentOption])
        delay(setDefaultValue, 10, currentOption)
      }

      if (props.options) {
        delay(setOptions, 100, props.options)
      }
      // console.log('initValue', { defaultValue, currentOption, options })
    }, [props.options])

    useEffect(() => {
      // Update only if things have in fact changed
      if (!isEqual(props.options, options)) {
        delay(setOptions, 100, props.options || [])
      }
    }, [props.options])

    const reset = () => {
      handleChange(new Event('change'), { value: '' })
    }

    const inputVariant = ({ variant, ...props }) => {
      switch (variant) {
        case 'filled':
          return <FilledInput {...props} />
        case 'standard':
          return <Input {...props} />
        case 'outlined':
        default:
          return <OutlinedInput {...props} />
      }
    }

    return (
      <>
        {style === Forms.SelectStyles.autocomplete ? (
          defaultValue !== null && (
            <Autocomplete
              {...autocompleteDefaultProps}
              {...commonProps}
              onChange={handleChange}
              onBlur={handleChange}
              defaultValue={defaultValue}
              className={`${className} form-field__autocomplete`}
              freeSolo={freeSolo}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    placeholder={`Start typing to find a selection${
                      freeSolo ? ' or enter your own' : ''
                    }`}
                    name={name}
                    variant={variant}
                    label={label}
                    inputProps={{
                      ...extend({}, params.inputProps),
                      autoComplete: 'no',
                    }}
                  />
                )
              }}
            />
          )
        ) : (
          <>
            {showLabelOnTop && <label htmlFor={selectId} className="form-field__label">{label}</label>}
            <FormControl className={`${className} form-field__select ${showLabelOnTop ? 'form-field__label-on-top' : ''}`} data-style={style} disabled={disabled} variant={variant}>
              <InputLabel htmlFor={selectId}>{showLabelOnTop ? placeholder : label}</InputLabel>
              <MuiSelect
                {...renderAttributes}
                {...commonProps}
                inputProps={{
                  readOnly: disabled,
                }}
                input={inputVariant({ variant, label })}
                IconComponent={(props) => (<KeyboardArrowDown {...props} />)}
                MenuProps={MenuProps}
              >
                {options.map((item, index) => {
                  const common = {
                    key: uniqueId(`select-item-${index}_`),
                    className: 'form-field__select-option',
                    value: item.value ?? item.label,
                  }
                  let inner = item.label
                  switch (style) {
                    case Forms.SelectStyles.checkboxes:
                    case Forms.SelectStyles.multipleCheckboxes:
                      // console.log('item', item.label, itemValue)
                      inner = (
                        <Checkbox
                          label={item.label}
                          name={item.name || item.label}
                          checked={
                            formikValue && formikValue.indexOf(item.value || item.label) !== -1
                          }
                          labelPlacement={Forms.LabelPlacement.end}
                        />
                      )
                  }

                  // eslint-disable-next-line react/jsx-key
                  return <MenuItem {...common}>{inner}</MenuItem>
                })}
              </MuiSelect>
            </FormControl>
          </>
        )}
        <button
          className="form-field__reset"
          data-name={name}
          type="button"
          aria-hidden
          onClick={reset}
        />
      </>
    )
  }
)

// Export Props
Select.defaultProps = defaultProps
Select.propTypes = propTypes
// Export Enums
// Export Component
export default Select
